import React, { useEffect, useState } from "react";
import AdminDashboard from "./admindashboard";
import axios from "../api/baseurl";
import { useNavigate } from "react-router-dom";
import "./Spinner.css";
import "./Loader.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiInfo } from "react-icons/fi";
import TestForPackage from "./testsByPackagePopup";
import { Pagination } from "react-bootstrap";
import { FaDownload, FaEdit, FaRedoAlt } from 'react-icons/fa';
import PemeAppointmentReschedule from "./pemeAppointmentReschedule";
import PemeAppointmentCancel from "./pemeAppointmentCancellation";
import UploadPemeReport from "./uploadPemeAppointmentReport";
import "./hm-theme.css";

function PemeAppointments() {
  const [appointmentDetails, setAppointmentDetails] = useState([]);
  const [loader, setLoader] = useState(true);
  const [isReschedulePopupOpen, setIsReschedulePopupOpen] = useState(false);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [companies, setCompanies] = useState([]);
  const [labs, setLabs] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [selectedLab, setSelectedLab] = useState("all");

  const [isTestsModalOpen, setIsTestsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const userRole = JSON.parse(localStorage.getItem("data")).role;
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("data")).token;

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedCompany, selectedLab]);

  useEffect(() => {
    if (searchTerm || selectedCompany !== "all" || selectedLab !== "all") {
      searchData(currentPage);
    } else {
      getData(currentPage);
    }
  }, [searchTerm, selectedCompany, selectedLab, currentPage]);

  const searchData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const searchUrl = `/api/peme-appointment/seachPemeAppointments`;
      const requestData = {
        labId: selectedLab === "all" ? null : selectedLab,
        companyId: selectedCompany === "all" ? null : selectedCompany,
        searchTerm: searchTerm || null,
      };
      const response = await axios.post(searchUrl, requestData, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      setAppointmentDetails(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      handleError(error);
    } finally {
      setLoader(false);
    }
  };

  const getData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const trans = await axios.get(`/api/peme-appointment/getAllPemeAppoinments`, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      setAppointmentDetails(trans.data.content || []);
      setCurrentPage(trans.data.number || 0);
      setTotalPages(trans.data.totalPages || 0);

      const response = await axios.get("/api/companydetails/getCompanies", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(response.data || []);

      const labResponse = await axios.get("/api/labdetails/getRegulatedLabsForPEME", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLabs(labResponse.data || []);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleViewReport = (reportId) => {
    const url = `/api/peme-appointment-reports/view/${reportId}`;
    axios({
      url,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      window.open(blobUrl, "_blank");
    }).catch(() => toast.error("Failed to view report."));
  };

  const handleDownloadReport = (reportId, fileName) => {
    axios({
      url: `/api/peme-appointment-reports/view/${reportId}`,
      method: "GET",
      responseType: "blob",
      headers: { Authorization: `Bearer ${token}` },
    }).then((response) => {
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName || "report");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    }).catch(() => toast.error("Failed to download report."));
  };

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(error.response?.data?.message || error.message || "An error occurred.");
    }
  }

  const openTestsModal = (pkg) => {
    setSelectedPackage(pkg);
    setIsTestsModalOpen(true);
  };

  const handleCancelAppointment = (appointment) => {
    setSelectedAppointment(appointment);
    setIsCancelPopupOpen(true);
  };

  const handleUploadReport = (appointmentId) => {
    setSelectedAppointmentId(appointmentId);
    setIsUploadModalOpen(true);
  };

  const handleRetry = async (appointment) => {
    setLoader(true);
    try {
      await axios.post('/api/peme-appointment/re-trigger-api', appointment, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Retried successfully');
      getData(currentPage);
    } catch {
      toast.error('Retry failed');
    } finally {
      setLoader(false);
    }
  };

  const handleFetchReport = async (appointment) => {
    setLoader(true);
    try {
      await axios.post('/api/peme-appointment/fetchReport', appointment, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Report fetched successfully');
      getData(currentPage);
    } catch (error) {
      handleError(error);
    } finally {
      setLoader(false);
    }
  };

  const handleRescheduleAppointment = (appointmentId) => {
    setSelectedAppointmentId(appointmentId);
    setIsReschedulePopupOpen(true);
  };

  const getStatusBadge = (status) => {
    if (!status) return "hm-badge hm-badge-info";
    const s = status.toUpperCase();
    if (s.includes("COMPLETED") || s.includes("APPROVED")) return "hm-badge hm-badge-success";
    if (s.includes("CANCEL")) return "hm-badge hm-badge-danger";
    if (s.includes("PENDING") || s.includes("REQUEST")) return "hm-badge hm-badge-warning";
    return "hm-badge hm-badge-info";
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  return (
    <div>
      <AdminDashboard />
      {loader && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">

          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">medical_services</i>
                PEME Appointments
              </h2>
              <p className="hm-page-subtitle">Pre-Employment Medical Examination appointment management</p>
            </div>
            <button className="btn-hm-primary" onClick={() => navigate('/users/book-peme-appointments')}>
              <i className="material-icons" style={{ fontSize: "1rem" }}>event_available</i>
              Book PEME Appointment
            </button>
          </div>

          {/* Filter Card */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">filter_alt</i>
                Search & Filter
              </h3>
            </div>
            <div className="hm-card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Search</label>
                  <div className="position-relative">
                    <input
                      type="search"
                      className="form-control ps-4"
                      placeholder="Search by name or phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="material-icons position-absolute text-muted" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "1.1rem" }}>
                      search
                    </i>
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Filter by Company</label>
                  <select
                    className="form-select"
                    value={selectedCompany || "all"}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                  >
                    <option value="all">All Companies</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.companyname}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Filter by Lab</label>
                  <select
                    className="form-select"
                    value={selectedLab || "all"}
                    onChange={(e) => setSelectedLab(e.target.value)}
                  >
                    <option value="all">All Labs</option>
                    {labs.map((lab) => (
                      <option key={lab.id} value={lab.id}>
                        {lab.labname} ({lab.labcode})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Appointments Table Card */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">list_alt</i>
                PEME Appointments List
              </h3>
              <span className="hm-badge hm-badge-primary">{appointmentDetails.length} records</span>
            </div>
            <div className="hm-card-body p-0">
              <div style={{ overflowX: "auto" }}>
                <table className="hm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Candidate Name</th>
                      <th>Phone / Email</th>
                      <th>Date</th>
                      <th>Lab</th>
                      <th>Company</th>
                      <th>Package</th>
                      <th>Status & Actions</th>
                      <th>Reports</th>
                      <th>Lab Order Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointmentDetails.length === 0 ? (
                      <tr>
                        <td colSpan="10">
                          <div className="hm-empty-state">
                            <i className="material-icons">event_busy</i>
                            <p>{loader ? "Loading PEME appointments..." : "No PEME appointments found"}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      appointmentDetails.map((appointment, index) => (
                        <tr key={appointment.id}>
                          <td>{currentPage * 10 + index + 1}</td>
                          <td className="fw-semibold">
                            {appointment.pemeDetails?.fullName || "—"}
                          </td>
                          <td>
                            <div>{appointment.pemeDetails?.phoneNumber || "—"}</div>
                            <div className="text-muted small">{appointment.pemeDetails?.email}</div>
                          </td>
                          <td>{appointment.appointmentDate || "—"}</td>
                          <td>{appointment.labs?.labname} ({appointment.labs?.labcode})</td>
                          <td>{appointment.company?.companyname || "—"}</td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <span>{appointment.pack?.packagename || "—"}</span>
                              {appointment.packageid && (
                                <FiInfo
                                  onClick={() => openTestsModal(appointment.packageid)}
                                  style={{ cursor: "pointer", color: "#2cb6e9" }}
                                  title="View Package Tests"
                                />
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="mb-1">
                              <span className={getStatusBadge(appointment.appStatus?.appointmentStatus)}>
                                {appointment.appStatus?.appointmentStatus}
                              </span>
                            </div>
                            {appointment.appStatus?.appointmentStatus !== 'APPOINTMENT CANCELLED' && appointment.appStatus?.appointmentStatus !== 'APPOINTMENT COMPLETED' && (
                              <div className="d-flex gap-1 mt-1">
                                <button
                                  className="btn btn-outline-danger btn-sm rounded-pill py-0 px-2"
                                  style={{ fontSize: "0.75rem" }}
                                  onClick={() => handleCancelAppointment(appointment)}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="btn btn-outline-warning btn-sm rounded-pill py-0 px-2"
                                  style={{ fontSize: "0.75rem" }}
                                  onClick={() => handleRescheduleAppointment(appointment.id)}
                                >
                                  Reschedule
                                </button>
                              </div>
                            )}
                          </td>
                          <td>
                            {appointment.appStatus?.appointmentStatus === 'APPOINTMENT CANCELLED' ? (
                              <span className="text-muted small">N/A</span>
                            ) : appointment.orderStatus?.appointmentReport ? (
                              appointment.reportstatus === 'APPROVED' ? (
                                <a href={appointment.orderStatus.appointmentReport} target="_blank" rel="noopener noreferrer" className="hm-badge hm-badge-success text-decoration-none">
                                  View
                                </a>
                              ) : (
                                <span className="hm-badge hm-badge-warning">Pending Approval</span>
                              )
                            ) : (appointment.reports || []).length > 0 ? (
                              <ul className="list-unstyled mb-0">
                                {appointment.reports.map((report, ri) => (
                                  <li key={report.id} className="d-flex align-items-center gap-1 mb-1">
                                    <span
                                      className="text-primary"
                                      style={{ cursor: "pointer", fontSize: "0.75rem" }}
                                      onClick={() => handleViewReport(report.id)}
                                    >
                                      {report.fileName || `File ${ri + 1}`}
                                    </span>
                                    <FaDownload
                                      size={12}
                                      style={{ cursor: "pointer", color: "#198754" }}
                                      onClick={() => handleDownloadReport(report.id, report.fileName)}
                                    />
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted small">No Report</span>
                            )}
                          </td>
                          <td>
                            <div>{appointment.orderStatus?.status || "N/A"}</div>
                            {appointment.orderStatus?.orderId === "" || appointment.orderStatus?.orderId == null ? (
                              appointment.appStatus?.appointmentStatus !== 'APPOINTMENT CANCELLED' && (
                                <button
                                  className="btn btn-link btn-sm text-danger p-0 d-flex align-items-center gap-1"
                                  onClick={() => handleRetry(appointment)}
                                >
                                  Retry <FaRedoAlt size={10} />
                                </button>
                              )
                            ) : (
                              <code>{appointment.orderStatus?.orderId}</code>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center py-3">
                  <Pagination className="hm-pagination">
                    <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                    {renderPageNumbers()}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
                  </Pagination>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {isTestsModalOpen && (
        <TestForPackage
          isOpen={isTestsModalOpen}
          onRequestClose={() => setIsTestsModalOpen(false)}
          packageId={selectedPackage}
        />
      )}
      {isReschedulePopupOpen && (
        <PemeAppointmentReschedule
          isOpen={isReschedulePopupOpen}
          onRequestClose={() => { setIsReschedulePopupOpen(false); getData(currentPage); }}
          appointmentId={selectedAppointmentId}
        />
      )}
      {isCancelPopupOpen && (
        <PemeAppointmentCancel
          isOpen={isCancelPopupOpen}
          onRequestClose={() => { setIsCancelPopupOpen(false); getData(currentPage); }}
          appointment={selectedAppointment}
        />
      )}
      {isUploadModalOpen && (
        <UploadPemeReport
          isOpen={isUploadModalOpen}
          onRequestClose={() => { setIsUploadModalOpen(false); getData(currentPage); }}
          pemeAppointmentId={selectedAppointmentId}
        />
      )}
    </div>
  );
}

export default PemeAppointments;
